import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, type Persona } from '../lib/auth';
import { ErrorNote, Spinner } from '../ui/components';

const LOGIN_ERRORS: Record<string, string> = {
  no_access:
    'You signed in with One Citizen, but this account has no Foreign Affairs staff access yet. Ask your division administrator to grant you a role.',
  access_denied: 'One Citizen sign-in was cancelled.',
  idp_unreachable: 'One Citizen is not reachable right now — try again shortly.',
  session_expired: 'The sign-in attempt expired — please try again.',
  state_mismatch: 'The sign-in attempt could not be validated — please try again.',
  token_exchange_failed: 'One Citizen rejected the sign-in — please try again.',
  signin_failed: 'Sign-in failed — please try again.',
};

const ROLE_ORDER = ['PROTOCOL_OFFICER', 'MISSION_HEAD', 'CONSULAR_OFFICER', 'APOSTILLE_AUTHORITY'] as const;
type RoleGroup = (typeof ROLE_ORDER)[number];

const ROLE_META: Record<RoleGroup, { label: string; blurb: string }> = {
  PROTOCOL_OFFICER: {
    label: 'Protocol desk (HQ)',
    blurb: 'Maintain the mission directory, vet and accredit diplomatic staff, and run VIP-lounge requests.',
  },
  MISSION_HEAD: {
    label: 'Heads of mission',
    blurb: 'Supervise consular casework at your mission and attest overseas births, deaths & marriages before they forward to M01.',
  },
  CONSULAR_OFFICER: {
    label: 'Consular officers',
    blurb: 'Process passport-renewal referrals, emergency travel documents, welfare assistance & notarial acts for the diaspora.',
  },
  APOSTILLE_AUTHORITY: {
    label: 'Apostille & authentication',
    blurb: 'Verify documents against the issuing registry, collect the fee, and issue e-Apostilles with a QR-verifiable record.',
  },
};

function primaryGroup(p: Persona): RoleGroup {
  return ROLE_ORDER.find((r) => p.roles.includes(r)) ?? 'CONSULAR_OFFICER';
}

export function Login() {
  const { personas, config, login, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loginError = params.get('login_error');
  const loginErrorText = loginError ? (LOGIN_ERRORS[loginError] ?? LOGIN_ERRORS.signin_failed) : null;

  const enter = async (p: Persona) => {
    setBusy(p.id);
    setError(null);
    try {
      await login(p.id);
      navigate('/');
    } catch {
      setError('Sign-in failed — is the gateway running?');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-ink-950">
      <div className="mx-auto max-w-5xl px-6 py-12">
        <header className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600 text-2xl font-black text-white">
            SC
          </div>
          <h1 className="text-2xl font-black tracking-tight text-white">One Gov Foreign Affairs & Diaspora</h1>
          <p className="mt-1 text-sm text-slate-400">
            Ministry of Foreign Affairs — consular services, diplomatic protocol & diaspora engagement staff console
          </p>
        </header>

        {/* Public e-Apostille verification needs no sign-in at all — surfaced
            prominently above the staff sign-in options, exactly like a real
            government portal would put it front-and-centre for foreign
            registries and border officials. */}
        <div className="mx-auto mb-10 max-w-xl rounded-xl border border-emerald-700/40 bg-emerald-900/20 p-5 text-center">
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-400">No sign-in required</div>
          <h2 className="mt-1 text-lg font-bold text-white">Verify an e-Apostille</h2>
          <p className="mt-1 text-sm text-emerald-100/80">
            Foreign registries, employers & border officials can check any Seychelles e-Apostille here — in English, French or Kreol.
          </p>
          <a
            href="/verify"
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-emerald-500"
          >
            Verify a document ↗
          </a>
        </div>

        {loginErrorText ? (
          <div className="mx-auto mb-6 max-w-xl">
            <ErrorNote message={loginErrorText} />
          </div>
        ) : null}
        {error ? (
          <div className="mx-auto mb-6 max-w-xl">
            <ErrorNote message={error} />
          </div>
        ) : null}

        {config.oneCitizen ? (
          <div className="mx-auto mb-10 max-w-md text-center">
            <a
              href="/auth/onecitizen/start"
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-brand-600 px-5 py-3.5 text-base font-bold text-white shadow-lg shadow-brand-600/25 transition hover:bg-brand-700"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-xs font-black">1</span>
              Sign in with One Citizen
            </a>
            <p className="mt-2 text-xs text-slate-500">
              Your national digital identity. Staff roles are granted by your division administrator.
            </p>
          </div>
        ) : null}

        {loading ? <Spinner label="Loading sign-in options…" /> : null}

        {config.devLogin && personas.length > 0 ? (
          <p className="mb-4 text-center text-xs font-semibold uppercase tracking-wider text-amber-400/90">
            Development personas — disabled in production (ONEGOV_DEV_LOGIN=0)
          </p>
        ) : null}

        {ROLE_ORDER.filter(() => config.devLogin).map((group) => {
          const items = personas.filter((p) => primaryGroup(p) === group);
          if (items.length === 0) return null;
          return (
            <section key={group} className="mb-8">
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300">{ROLE_META[group].label}</h2>
              <p className="mb-3 mt-0.5 text-xs text-slate-500">{ROLE_META[group].blurb}</p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => enter(p)}
                    disabled={busy !== null}
                    className="rounded-xl border border-ink-700 bg-ink-900 p-4 text-left transition hover:border-brand-500 hover:bg-ink-800 disabled:opacity-60"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white">{p.name}</span>
                      {busy === p.id ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-500 border-t-white" />
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-xs font-medium text-brand-100">{p.title}</div>
                    <div className="mt-1 text-xs text-slate-400">
                      {p.org}
                      {p.missionCode ? <span className="text-slate-500"> · {p.missionCode.replaceAll('_', ' ')}</span> : null}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.roles.map((r) => (
                        <span key={r} className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 ring-1 ring-ink-700">
                          {r.replaceAll('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
